import { useCallback } from 'react';
import Notification from 'components/ui/base/notification/Notification';
import { DEFAULT_INTERNAL_ERROR_MESSAGE } from 'core/utils/constants';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { archiveInvoice as archiveInvoiceApi, unarchiveInvoice as unarchiveInvoiceApi } from 'store/invoices/client';
import { getErrorMessage } from 'utils/getErrorMessage';

export const useArchiveInvoice = (onSuccess?: () => void | Promise<void>) => {
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const runArchiveAction = useCallback(
        async (
            invoiceId: string,
            action: (invoiceId: string) => Promise<unknown>,
            loadingMessage: string,
            successMessage: string,
            errorMessage: string
        ) => {
            try {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: {
                        message: loadingMessage,
                    },
                });

                await action(invoiceId);

                Notification({
                    type: 'success',
                    placement: 'top',
                    messageTitle: 'Success',
                    description: successMessage,
                });

                await onSuccess?.();
            } catch (err) {
                const error = getErrorMessage(err);

                Notification({
                    type: 'error',
                    placement: 'top',
                    messageTitle: 'Operation Failed',
                    description: error || errorMessage,
                });
            } finally {
                hideGlobalOverlay();
            }
        },
        [onSuccess, showGlobalOverlay, hideGlobalOverlay]
    );

    const archiveInvoice = useCallback(
        (invoiceId: string) =>
            runArchiveAction(
                invoiceId,
                archiveInvoiceApi,
                'Archiving invoice',
                'Invoice archived successfully',
                DEFAULT_INTERNAL_ERROR_MESSAGE
            ),
        [runArchiveAction]
    );

    const unarchiveInvoice = useCallback(
        (invoiceId: string) =>
            runArchiveAction(
                invoiceId,
                unarchiveInvoiceApi,
                'Unarchiving invoice',
                'Invoice restored from the archives successfully',
                DEFAULT_INTERNAL_ERROR_MESSAGE
            ),
        [runArchiveAction]
    );

    return { archiveInvoice, unarchiveInvoice };
};
